// De vaste bouwstenen van een V1-pagina: kop met telling, filterkaart,
// tabelkaart, paginering en het inschuivende detailpaneel. Alle maten en
// kleuren komen uit docs/input/frontend/V1_frontend.jsx; dit bestand voegt geen
// ontwerp toe, het maakt het herbruikbaar.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { columnLabel } from "../tokens";
// Kleuren via de chattokens, zie SourceDetail (testfeedback 03-11, rij 6).
import { color, font, metric, radius, size, weight } from "./chatTokens";

/** h1 30px/700 met daaronder een TELLING in 15px #9ca3af — geen slogan. */
export function PageHeader({ title, count }: { title: string; count: ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h1
        style={{
          fontSize: size.pageTitle,
          fontWeight: weight.bold,
          color: color.ink,
          letterSpacing: "-0.025em",
          marginBottom: 4,
        }}
      >
        {title}
      </h1>
      <p style={{ fontSize: size.lead, color: color.faint }}>{count}</p>
    </div>
  );
}

/**
 * DE gedeelde sparkline (poetsronde 28-08): alleen de vorm van de reeks, geen
 * assen, eindpunt gemarkeerd; met `doortrekking` een gestippeld staartje (de
 * gemeten richting). Er bestonden drie sparkline-implementaties met drie
 * kleuren en drie diktes naast elkaar — de simpele lijn woont nu hier.
 * (KaartVisueel houdt bewust zijn eigen rijke variant met hulplijnen en
 * doortrekkingsband: dat is een grafiek, geen sparkline.)
 */
export function Sparklijn({
  waarden,
  w = 190,
  h = 34,
  doortrekking = [],
  style,
}: {
  waarden: readonly number[];
  w?: number;
  h?: number;
  doortrekking?: readonly number[];
  style?: CSSProperties;
}) {
  if (waarden.length < 2) return null;
  const alle = [...waarden, ...doortrekking];
  const min = Math.min(...alle);
  const max = Math.max(...alle);
  const bereik = max - min || 1;
  const totaal = alle.length;
  const punt = (v: number, i: number) => {
    const x = (i / Math.max(totaal - 1, 1)) * (w - 6) + 3;
    const y = h - 4 - ((v - min) / bereik) * (h - 10);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };
  const pad = waarden.map(punt).join(" ");
  const [lx, ly] = punt(waarden[waarden.length - 1]!, waarden.length - 1).split(",");
  const staart = doortrekking.length
    ? [waarden[waarden.length - 1]!, ...doortrekking]
        .map((v, i) => punt(v, waarden.length - 1 + i))
        .join(" ")
    : null;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      aria-hidden
      style={{ display: "block", flexShrink: 0, ...style }}
    >
      <polyline points={pad} fill="none" stroke={color.ink} strokeWidth={1.6} />
      {staart && (
        <polyline
          points={staart}
          fill="none"
          stroke={color.faint}
          strokeWidth={1.4}
          strokeDasharray="3 3"
        />
      )}
      <circle cx={lx} cy={ly} r={2.6} fill={color.ink} />
    </svg>
  );
}

/** Witte kaart, rand #ececec, radius 12 — de basisvorm van elk blok. */
export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * De filterbalk-kaart. Eén component omdat het ANKERPUNT van elk filtermenu
 * eraan hangt.
 *
 * FilterMenu zet zichzelf bewust op `position: static` en rendert zijn paneel
 * `position: absolute; left: 0; right: 0` — zo opent élk menu op dezelfde plek
 * en over de volle breedte (owner-review ronde 6). Dat werkt alleen als de
 * omhullende kaart een POSITIONED ancestor is. Trends, Skills en Arbeidsmarkt
 * schreven daar met de hand `position: relative`; Bronnen en Funding niet, en
 * dáár zweefde het paneel los van zijn knop (owner-opname 2026-08-25, 0:05).
 *
 * Een afspraak is geen mechanisme: zolang elk scherm zijn eigen kaart optuigt
 * komt dit terug. Daarom draagt deze component het anker, en gebruiken alle
 * lijstschermen hem.
 */
export function FilterBar({
  children,
  chips,
  style,
}: {
  children: ReactNode;
  /** Gekozen filters op een EIGEN regel eronder (owner-screenshot ronde 1). */
  chips?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      data-filterbar
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: metric.cardPadding,
        marginBottom: 16,
        // Het anker. Niet cosmetisch: hieraan meet elk filtermenu zich.
        position: "relative",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {children}
      </div>
      {chips}
    </div>
  );
}

/**
 * "Sorteren op" als dropdown in plaats van een rij tekstknoppen.
 *
 * Owner-punt 2026-08-25 (Trends): de knoppenrij leest als een filter terwijl er
 * altijd precies één actief is. Een select zegt dat met zijn vorm. Eén
 * component zodat de schermen die hem gebruiken er ook hetzelfde uitzien —
 * dat is de hele opdracht van de gelijktrek-ronde.
 */
export function SortSelect<T extends string>({
  value,
  options,
  onChange,
  label = "Sorteren op",
}: {
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (v: T) => void;
  label?: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <label
        htmlFor="sorteer-select"
        style={{ fontSize: size.meta, color: color.faint, whiteSpace: "nowrap" }}
      >
        {label}
      </label>
      <select
        id="sorteer-select"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          fontSize: size.meta,
          color: color.body,
          background: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: radius.input,
          padding: "6px 10px",
          fontFamily: "inherit",
          cursor: "pointer",
        }}
      >
        {options.map(([key, tekst]) => (
          <option key={key} value={key}>
            {tekst}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Kopregel van een tabel: hoogte 34, #fafafa, 10px/600 uppercase labels. */
export function TableHead({ cols, labels }: { cols: string; labels: readonly string[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: 0,
        alignItems: "center",
        padding: "0 16px",
        height: metric.tableHeaderHeight,
        background: color.surfaceAlt,
        borderBottom: `1px solid ${color.lineSoft}`,
      }}
    >
      {labels.map((label, i) => (
        <span
          key={label + i}
          style={{
            ...columnLabel,
            textTransform: "uppercase" as const,
            textAlign: i >= labels.length - 2 ? "center" : "left",
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

/** Paginering 36×36, radius 8, actief #111827. */
export function Pager({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  // Bij duizenden trends is een knop per pagina onwerkbaar: een venster van
  // zeven rond de huidige pagina houdt de dummy-vorm zonder honderd knoppen.
  const window = 7;
  let start = Math.max(1, page - Math.floor(window / 2));
  const end = Math.min(totalPages, start + window - 1);
  start = Math.max(1, end - window + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 6,
        marginTop: 16,
      }}
    >
      <button className="pg-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>
        ←
      </button>
      {start > 1 && <span style={{ color: color.faint, fontSize: size.meta }}>…</span>}
      {pages.map((p) => (
        <button
          key={p}
          className={"pg-btn" + (p === page ? " active-pg" : "")}
          onClick={() => onPage(p)}
        >
          {p}
        </button>
      ))}
      {end < totalPages && <span style={{ color: color.faint, fontSize: size.meta }}>…</span>}
      <button className="pg-btn" disabled={page === totalPages} onClick={() => onPage(page + 1)}>
        →
      </button>
    </div>
  );
}

/**
 * Het huispatroon voor detail: scrim met blur eroverheen, paneel schuift van
 * rechts in. Timing exact als de dummy — 20 ms voor het openen (zodat de
 * transitie pakt) en 420 ms voor het opruimen na het sluiten.
 */
export function SlidePanel({
  open,
  onClose,
  children,
  /** 2 = een paneel dat OVER een ander paneel schuift (skill -> zijn bron).
   *  Zonder eigen laag zou het tweede paneel achter de waas van het eerste
   *  verdwijnen en lijkt de doorklik kapot. */
  level = 1,
  /** B8 (owner-screenshots 27-08): één paneelidioom, overal. Met een label
   *  krijgt het paneel dezelfde terugbalk als het in-chat paneel ("← Terug
   *  naar de lijst"); het kruisje in de PanelHeader blijft ernaast bestaan,
   *  precies zoals in de screenshots. */
  terugLabel,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  level?: 1 | 2;
  terugLabel?: string;
}) {
  const [mounted, setMounted] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => setMounted(true), metric.panelOpenDelay);
      timers.current.push(t);
    } else {
      setMounted(false);
    }
    return () => {
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, [open]);

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: metric.scrim,
            zIndex: level === 2 ? 140 : 40,
            backdropFilter: metric.scrimBlur,
          }}
        />
      )}
      <div
        className={"slide-panel " + (mounted ? "open" : "")}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: metric.panelWidth,
          background: color.surface,
          borderLeft: `1px solid ${color.line}`,
          zIndex: level === 2 ? 150 : 50,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {open && terugLabel ? (
          <button
            onClick={onClose}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: color.surfaceAlt,
              border: "none",
              borderBottom: `1px solid ${color.lineFaint}`,
              padding: "9px 16px",
              font: "inherit",
              fontSize: 12,
              fontWeight: 500,
              color: color.muted,
              cursor: "pointer",
              textAlign: "left",
              flexShrink: 0,
            }}
          >
            ← {terugLabel}
          </button>
        ) : null}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{open && children}</div>
      </div>
    </>
  );
}

/** Sluitkruis in de vaste kop van een detailpaneel. */
export function PanelHeader({
  children,
  onClose,
}: {
  children?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: color.surface,
        borderBottom: `1px solid ${color.lineFaint}`,
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {children}
      </div>
      <button
        onClick={onClose}
        aria-label="Paneel sluiten"
        style={{
          background: "none",
          border: "none",
          color: color.faint,
          cursor: "pointer",
          padding: 4,
          lineHeight: 0,
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
          <path
            d="M2 2l10 10M12 2L2 12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

/** Klein blokkopje binnen een paneel: 11px/600 uppercase #9ca3af. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: size.label,
        fontWeight: weight.medium,
        color: color.faint,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

/** Een oordeel-met-reden: pill met tooltip. Nooit een badge zonder uitleg. */
export function ReasonBadge({
  text,
  tone = "neutral",
  title,
}: {
  text: string;
  tone?: "neutral" | "amber" | "blue";
  title?: string;
}) {
  const tones = {
    neutral: { bg: color.lineFaint, fg: color.soft, br: color.line },
    amber: { bg: color.accentAmberBg, fg: color.accentAmber, br: "var(--chat-amberRand, #fde68a)" },
    blue: { bg: "var(--chat-blauwVlak, #eff6ff)", fg: color.accentBlue, br: "var(--chat-blauwRand, #bfdbfe)" },
  } as const;
  const t = tones[tone];
  return (
    <span
      title={title ?? text}
      style={{
        fontSize: 10,
        fontWeight: weight.medium,
        color: t.fg,
        background: t.bg,
        border: `1px solid ${t.br}`,
        borderRadius: radius.pill,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

/**
 * Scorebalkje in een tabelrij: 32px spoor, gekleurde vulling, cijfer ernaast.
 * `title` draagt de reden achter de score — W8-regel 2 wil nooit een kale score,
 * maar de owner wil de rij schoon (opname 2026-08-24), dus de reden is
 * opvraagbaar in plaats van altijd zichtbaar.
 */
export function ScoreCell({
  value,
  tone,
  title,
}: {
  value: number | undefined;
  tone: string;
  title?: string;
}) {
  if (typeof value !== "number") {
    return <span style={{ fontSize: 11, color: color.faint }}>—</span>;
  }
  return (
    <div title={title} style={{ display: "flex", alignItems: "center", gap: 3 }}>
      <div style={{ width: 32, height: 3, background: color.lineSoft, borderRadius: 99 }}>
        <div
          style={{
            height: "100%",
            width: `${Math.max(0, Math.min(10, value)) * 10}%`,
            background: tone,
            borderRadius: 99,
          }}
        />
      </div>
      <span style={{ fontSize: 11, fontWeight: weight.medium, color: color.body }}>
        {value.toFixed(1)}
      </span>
    </div>
  );
}

/** Scoreregel in een detailpaneel (ScoreBar uit de dummy, r. 671). */
export function ScoreBar({
  label,
  value,
  muted,
}: {
  label: string;
  value: number | undefined;
  muted?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: size.meta, color: color.faint, width: 60, flexShrink: 0 }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 3, background: color.lineSoft, borderRadius: 99 }}>
        <div
          style={{
            height: "100%",
            width: `${typeof value === "number" ? Math.max(0, Math.min(10, value)) * 10 : 0}%`,
            background: muted ? color.disabled : color.ink,
            borderRadius: 99,
          }}
        />
      </div>
      <span
        style={{
          fontSize: size.body,
          fontWeight: weight.medium,
          color: muted ? color.faint : color.body,
          minWidth: 28,
          textAlign: "right",
        }}
      >
        {typeof value === "number" ? value.toFixed(1) : "—"}
      </span>
    </div>
  );
}

// ── Uitleg bij de hand ───────────────────────────────────────────────────────
//
// Tot nu toe hing élke uitleg aan een `title=`-attribuut. Dat werkt, maar de
// browser toont hem pas na een seconde, in systeemgrijs, buiten de huisstijl,
// en op een aanraakscherm helemaal niet. De owner vroeg om "het i'tje met mouse
// over" (Skills #1) en om de filter-omschrijvingen daarin (Bronnen #3).
// Vorm letterlijk uit de dummy, V1 r. 682-726.

/** Donker kaartje boven het element waar de muis op staat. */
/**
 * Donker kaartje boven het element waar de muis op staat.
 *
 * HET KAARTJE HANGT IN EEN PORTAAL AAN `document.body`, en dat is geen
 * netheid maar een reparatie. Owner-screenshot 2026-08-25: de uitleg verscheen
 * ver naast het i'tje. Nagemeten in de browser — het i'tje op (267, 305):
 *
 *     zonder hover op de kaart:  tooltip op (156, 225)   klopt
 *     mét hover op de kaart:     tooltip op (193, 513)   288 px te laag
 *
 * Oorzaak: `.chart-card:hover` zet `transform: translateY(-2px)`, en volgens de
 * CSS-spec maakt een `transform` op een voorouder díé voorouder het
 * referentiekader voor `position: fixed`. De viewport-coördinaten werden dus
 * als kaart-coördinaten gelezen. En je raakt die kaart altijd aan, want het
 * i'tje zít erop. Een portaal heeft per definitie geen getransformeerde
 * voorouder, dus het probleem kan niet terugkomen — ook niet als er later
 * ergens anders een transform bij komt.
 *
 * Twee dingen die de screenshot ook liet zien en die hier meteen bij horen: het
 * kaartje blijft binnen het scherm, en het klapt naar ónder het element als er
 * bovenaan geen ruimte is.
 */
export function Tooltip({
  text,
  children,
  maxWidth = 220,
}: {
  text: ReactNode;
  children: ReactNode;
  maxWidth?: number;
}) {
  const [pos, setPos] = useState<{ top: number; left: number; onder: boolean } | null>(
    null,
  );
  const ref = useRef<HTMLSpanElement>(null);

  const toon = () => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    const marge = 8;
    // Ruwe hoogteschatting: genoeg om te bepalen of hij boven past. Preciezer
    // meten zou een tweede render kosten en dat ziet de gebruiker als geflikker.
    const hoogte = 120;
    const onder = box.top < hoogte + marge;
    const half = maxWidth / 2;
    const midden = box.left + box.width / 2;
    setPos({
      // Binnen het scherm houden: een i'tje aan de rechterrand duwde het
      // kaartje anders half buiten beeld.
      left: Math.min(Math.max(midden, half + marge), window.innerWidth - half - marge),
      top: onder ? box.bottom + marge : box.top - marge,
      onder,
    });
  };

  return (
    <span
      ref={ref}
      onMouseEnter={toon}
      onMouseLeave={() => setPos(null)}
      style={{ position: "relative", display: "inline-flex" }}
    >
      {children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform: `translate(-50%, ${pos.onder ? "0" : "-100%"})`,
              background: "#1f2937",
              color: "white",
              fontSize: 12,
              lineHeight: 1.55,
              padding: "8px 12px",
              borderRadius: 8,
              width: maxWidth,
              zIndex: 9999,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              pointerEvents: "none",
              whiteSpace: "normal",
              textTransform: "none",
              letterSpacing: 0,
              fontWeight: weight.regular,
              fontFamily: font,
            }}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

/**
 * Dezelfde zwevende uitleg, maar voor elementen die geen `<span>` om zich heen
 * kunnen hebben — SVG-vormen zoals de bolletjes van de prioriteitsmatrix en de
 * staven van het histogram.
 *
 * Gebruik: `const zweef = useZweefKaart()` en dan per vorm
 * `onMouseEnter={(e) => zweef.toon(e.currentTarget, <inhoud/>)}` plus
 * `onMouseLeave={zweef.verberg}`; render `{zweef.kaart}` ergens in de boom.
 * De kaart gaat door hetzelfde portaal als Tooltip, om dezelfde reden.
 */
export function useZweefKaart(breedte = 260) {
  const [staat, setStaat] = useState<{
    top: number;
    left: number;
    onder: boolean;
    inhoud: ReactNode;
  } | null>(null);

  const toon = (el: Element, inhoud: ReactNode) => {
    const box = el.getBoundingClientRect();
    const marge = 10;
    const onder = box.top < 160;
    const half = breedte / 2;
    setStaat({
      left: Math.min(
        Math.max(box.left + box.width / 2, half + marge),
        window.innerWidth - half - marge,
      ),
      top: onder ? box.bottom + marge : box.top - marge,
      onder,
      inhoud,
    });
  };

  const kaart = staat
    ? createPortal(
        <span
          role="tooltip"
          style={{
            position: "fixed",
            top: staat.top,
            left: staat.left,
            transform: `translate(-50%, ${staat.onder ? "0" : "-100%"})`,
            background: "#1f2937",
            color: "white",
            fontSize: 12,
            lineHeight: 1.55,
            padding: "10px 12px",
            borderRadius: 8,
            width: breedte,
            zIndex: 9999,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            fontFamily: font,
          }}
        >
          {staat.inhoud}
        </span>,
        document.body,
      )
    : null;

  return { toon, verberg: () => setStaat(null), kaart };
}

/** Het i-tje: klein vierkantje dat zijn uitleg toont zodra je erop staat. */
export function InfoIcon({ tooltip, maxWidth }: { tooltip: ReactNode; maxWidth?: number }) {
  const [hover, setHover] = useState(false);
  return (
    <Tooltip text={tooltip} maxWidth={maxWidth ?? 240}>
      <span
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label="uitleg"
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          border: `1.5px solid ${hover ? color.faint : color.border}`,
          background: hover ? color.surfaceQuote : color.surface,
          color: hover ? color.soft : color.disabled,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          fontWeight: weight.bold,
          fontStyle: "normal",
          cursor: "help",
          transition: "all 0.15s",
          flexShrink: 0,
        }}
      >
        i
      </span>
    </Tooltip>
  );
}

/** De uitklapknop op een grafiekkaart (V1 r. 728-739). */
export function ExpandButton({ onClick, label }: { onClick: () => void; label: string }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 20,
        height: 20,
        borderRadius: 5,
        border: `1.5px solid ${hover ? color.faint : color.border}`,
        background: hover ? color.surfaceQuote : color.surface,
        color: hover ? color.soft : color.disabled,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.15s",
        flexShrink: 0,
        padding: 0,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
        <path
          d="M9 1h4v4M5 13H1V9M13 1L8.5 5.5M1 13l4.5-4.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * Groot venster over de pagina (V1 `ChartModal`, r. 1257-1265). Sluit op de
 * achtergrond, op het kruisje en op Escape — alle drie, want een venster dat je
 * maar op één manier kwijtraakt voelt als een val.
 */
export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.28)",
        backdropFilter: "blur(2px)",
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: color.surface,
          borderRadius: radius.card,
          padding: 24,
          width: "min(880px, 100%)",
          maxHeight: "88vh",
          overflowY: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 18,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: weight.bold, color: color.ink }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: size.meta, color: color.faint, marginTop: 3 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Sluiten"
            style={{
              background: color.lineFaint,
              border: "none",
              borderRadius: radius.input,
              width: 30,
              height: 30,
              cursor: "pointer",
              color: color.soft,
              flexShrink: 0,
              lineHeight: 0,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * De zoekregel uit de dummy (V1 r. 1721). Filtert de lijst zelf — anders dan
 * het zoekveld op Trends, dat een readOnly-knop naar de zoekoverlay is.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  width = 230,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  width?: number;
}) {
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        style={{
          position: "absolute",
          left: 12,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        <circle cx="6" cy="6" r="4" stroke={color.faint} strokeWidth="1.5" />
        <path d="M10 10l2 2" stroke={color.faint} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        className="search-input"
        style={{ width }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          onClick={() => onChange("")}
          aria-label="Zoekopdracht wissen"
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            color: color.disabled,
            cursor: "pointer",
            padding: 4,
            lineHeight: 0,
          }}
        >
          <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
            <path
              d="M2 2l10 10M12 2L2 12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
